import { motion } from "framer-motion";
import { ArrowUpRight, Plane, Coins, Check } from "lucide-react";
import { AEROVISTA_CLUB_URL, VISTACION_URL } from "@/constants";

const CARDS = [
  {
    icon: Plane,
    title: "Aero Vista Club",
    description:
      "Your gateway to private aviation, luxury yacht brokerage, aircraft acquisition, charter services, and global wealth opportunities.",
    items: [
      "Aircraft Sales",
      "Aircraft Acquisition",
      "Private Jet Charter",
      "Yacht Brokerage",
      "Luxury Yacht Acquisition",
      "Bullion & Commodities",
      "Client Representation",
    ],
    button: "Visit Aero Vista Club",
    href: AEROVISTA_CLUB_URL,
    external: false,
    testid: "ecosystem-card-club",
    buttonTestid: "ecosystem-visit-club-button",
  },
  {
    icon: Coins,
    title: "Vistacion (VSC)",
    description:
      "A next-generation blockchain and digital trade ecosystem supporting secure transactions, digital commerce, membership services, and future staking and governance functionality.",
    items: [
      "Vistacoin (VSC)",
      "Member Dashboard",
      "Digital Trade Solutions",
      "Blockchain Ecosystem",
      "Secure Transactions",
      "Future Staking",
      "Governance Platform",
    ],
    button: "Access Vistacion Platform",
    href: VISTACION_URL,
    external: true,
    testid: "ecosystem-card-vistacion",
    buttonTestid: "ecosystem-access-vistacion-button",
  },
];

export const Ecosystem = () => {
  return (
    <section
      id="ecosystem"
      data-testid="ecosystem-section"
      className="py-24 lg:py-32 bg-surface border-b border-white/5 scroll-mt-16"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-16 max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">
            Two Platforms · One Group
          </p>
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
            The Aerovista Ecosystem
          </h2>
          <p className="mt-6 text-base sm:text-lg text-white/70 font-light leading-relaxed">
            Aerovista Group combines luxury aviation, yacht brokerage,
            commodities trading, and digital innovation through two connected
            platforms designed to serve international clients, investors, and
            high-net-worth individuals.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {CARDS.map((card, i) => (
            <motion.article
              key={card.title}
              data-testid={card.testid}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              className="group relative rounded-sm border border-white/5 bg-obsidian p-8 lg:p-10 flex flex-col transition-transform duration-500 hover:-translate-y-1 hover:border-gold/40"
            >
              <card.icon
                size={26}
                strokeWidth={1.25}
                className="text-gold mb-6"
              />
              <h3 className="font-serif font-light text-2xl lg:text-3xl text-white mb-4">
                {card.title}
              </h3>
              <p className="text-sm text-white/60 font-light leading-relaxed mb-8">
                {card.description}
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-10">
                {card.items.map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-3 text-sm text-white/75 font-light"
                  >
                    <Check size={13} className="text-gold shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-auto">
                <a
                  href={card.href}
                  {...(card.external
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                  data-testid={card.buttonTestid}
                  className="group/btn inline-flex items-center gap-3 border border-gold/60 text-gold text-xs uppercase tracking-[0.2em] px-7 py-3.5 rounded-sm transition-colors duration-300 hover:bg-gold hover:text-black"
                >
                  {card.button}
                  <ArrowUpRight
                    size={14}
                    className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5"
                  />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
